import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { userAttachmentEditionService } from "../services/userAttachmentEditionService";
import {
  UserAttachmentEditionValuesInit,
  getUserAttachmentEditionValues,
  UserAttachmentEditionValuesType,
  UserAttachmentEditionErrorsType,
  userAttachmentEditionDisplayId,
} from "./UserAttachmentEditionCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { UserAttachmentEdition } from "../../../api/graphql";
import { actualStatusList } from "../../common/types";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { uploadFailureReason } from "../../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { GridChangeSet } from "../../common/types";
import { restoredStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class UserAttachmentEditionUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: UserAttachmentEditionErrorsType = {};
  service = userAttachmentEditionService;
  userAttachmentEditionInstance: UserAttachmentEdition | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = UserAttachmentEditionValuesInit();
  nameError: string | undefined;
  fileError: string | undefined;
  notesError: string | undefined;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<UserAttachmentEditionValuesType>[] =
    [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<UserAttachmentEditionValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<UserAttachmentEditionValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadUserAttachmentEditionById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.userAttachmentEditionInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.userAttachmentEditionInstance) {
      this.entityValues = getUserAttachmentEditionValues(
        this.userAttachmentEditionInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda.
  get saveDisabled() {
    return this.isProcessing;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return userAttachmentEditionDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    if (this.userAttachmentEditionInstance) {
      return userAttachmentEditionDisplayId(
        this.userAttachmentEditionInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserAttachmentEditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupUserAttachmentEditionLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  returnedData(_data: any): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  fileRestoreFile(id: string) {
    const file = this.entityValues.__file_files.find((x) => x.id === id);
    if (!file) return;

    const newStatus = restoredStatus(file.__status__);

    const activeFiles = this.entityValues.__file_files.filter((x) =>
      actualStatusList.includes(x.__status__)
    );
    if (activeFiles.length >= 1 && newStatus !== file.__status__) {
      alert("No puede haber más de un archivo activo");
      return;
    }

    file.__status__ = newStatus;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *fileUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__file_files];
    const previousName = this.entityValues.file;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__file_files.push(newFile);
    this.entityValues.file = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__file_files = previousFiles;
      this.entityValues.file = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("file");
      } else {
        this.setFieldError(
          "file",
          this.langStore.UserAttachmentEditionFileLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("file");
    const fileIndex = this.entityValues.__file_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__file_files[fileIndex].ticket = outcome.ticket;
    }
  }

  fileCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__file_files) {
        if (file.id === id) {
          if (isOriginalCrudStatus(file.__status__)) {
            file.__status__ = "original-deleted";
          } else {
            toRemove = index;
          }
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__file_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as UserAttachmentEditionErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as UserAttachmentEditionErrorsType;
  }

  *fileDownload(props: any, fieldName: string): any {
    // En el modal anidado (subentidad editada desde el parent) el instance
    // no se carga (loadValuesFromRow): el id sale de los values de la fila.
    // Sin el fallback, la URL iba con instanceId=undefined → 404 silencioso.
    const instanceId =
      this.userAttachmentEditionInstance?.id ?? this.entityValues.id;
    yield downloadWithAuth(
      {
        entityName: "UserAttachmentEdition",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      },
      undefined,
      this.rootStore.activityStore
    );
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      // Mismo fallback que fileDownload: en el modal anidado el instance
      // no se carga y el id viene de los values de la fila.
      const instanceId =
        this.userAttachmentEditionInstance?.id ?? this.entityValues.id;
      const blob = yield fetchFileBlob({
        entityName: "UserAttachmentEdition",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  getRowFromInstance(instance: UserAttachmentEdition) {
    const row: any = {};
    row.version_ = instance.version_;
    row.id = instance.id;
    row.name = instance.name;
    row.notes = instance.notes;
    return row;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.userAttachmentEditionInstance = null;
    this.entityValues = UserAttachmentEditionValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(
    values: UserAttachmentEditionValuesType
  ): UserAttachmentEditionErrorsType {
    const errors: UserAttachmentEditionErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserAttachmentEditionNameLabel,
      };
    }

    if (
      values.__file_files.filter((f: any) =>
        actualStatusList.includes(f.__status__)
      ).length === 0
    ) {
      errors.file = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserAttachmentEditionFileLabel,
      };
    }
    {
      const pendingUpload = values.__file_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.file = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.UserAttachmentEditionFileLabel,
        };
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "UserAttachmentEditionUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      file: this.entityValues.file,
      notes: this.entityValues.notes,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveUserAttachmentEdition(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            this.resetPendingChanges();
            this.saveOriginalState();
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelUserAttachmentEdition(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getUserAttachmentEditionValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = UserAttachmentEditionValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    // Subentidad sin edición standalone: se persiste por el update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadUserAttachmentEditionById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
